import { TableOptions } from '@/types/table';
import { ExpandedState, PaginationState } from '@tanstack/react-table';
import { Dispatch, RefObject, SetStateAction, useEffect } from 'react';
import { TableProps } from '../Table';
import { getRowId } from '../utils/utils';

type ExpandedSyncParams = {
  defaultExpanded: ExpandedState | undefined;
  groupingKey: string;
  pageIndex: number;
  pageSize: number;
  setExpanded: Dispatch<SetStateAction<ExpandedState>>;
};

export const useExpandedSync = ({
  defaultExpanded,
  groupingKey,
  pageIndex,
  pageSize,
  setExpanded,
}: ExpandedSyncParams) => {
  useEffect(() => {
    setExpanded(defaultExpanded ?? {});
  }, [defaultExpanded, groupingKey, pageIndex, pageSize, setExpanded]);
};

type TableInteractionSyncParams<T> = {
  data: T[];
  filter: TableProps<T>['filter'];
  id: string;
  initialHorizontalScroll: TableOptions<T>['initialHorizontalScroll'];
  onRowSelection: TableOptions<T>['onRowSelection'];
  pageIndex: number;
  previousFilterRef: RefObject<TableProps<T>['filter']>;
  rowExtension: TableOptions<T>['rowExtension'];
  rowSelection: Record<number, boolean>;
  rowSelectionEnabled: boolean;
  selectedElement: T | undefined;
  selectedRow: string | undefined;
  setGlobalFilter: Dispatch<SetStateAction<TableProps<T>['filter']>>;
  setPagination: (id: string, x: Partial<PaginationState>) => void;
  setRowExpandedDelayed: Dispatch<SetStateAction<Record<string, boolean>>>;
  setRowSelection: Dispatch<SetStateAction<Record<number, boolean>>>;
  tableWrapperRef: RefObject<HTMLDivElement>;
};

export const useTableInteractionSync = <T>({
  data,
  filter,
  id,
  initialHorizontalScroll,
  onRowSelection,
  pageIndex,
  previousFilterRef,
  rowExtension,
  rowSelection,
  rowSelectionEnabled,
  selectedElement,
  selectedRow,
  setGlobalFilter,
  setPagination,
  setRowExpandedDelayed,
  setRowSelection,
  tableWrapperRef,
}: TableInteractionSyncParams<T>) => {
  useEffect(() => {
    if (rowSelectionEnabled && onRowSelection) {
      onRowSelection(selectedElement);
    }
  }, [rowSelection, onRowSelection, rowSelectionEnabled, selectedElement]);

  useEffect(() => {
    if (initialHorizontalScroll && tableWrapperRef.current) {
      tableWrapperRef.current.scrollLeft = initialHorizontalScroll;
    }
  }, [initialHorizontalScroll, tableWrapperRef]);

  useEffect(() => {
    const rowIndex = data.findIndex(dataRow => getRowId(dataRow) === selectedRow);
    setRowSelection(selectedRow && rowIndex > -1 ? { [rowIndex]: true } : {});
  }, [data, selectedRow, setRowSelection]);

  useEffect(() => {
    if (previousFilterRef.current !== filter) {
      setGlobalFilter(filter);
      setPagination(id, { pageIndex: 0 });
    }

    previousFilterRef.current = filter;
  }, [filter, id, previousFilterRef, setGlobalFilter, setPagination]);

  useEffect(() => {
    if (rowExtension) {
      setRowExpandedDelayed({});
    }
    if (rowSelectionEnabled) {
      setRowSelection({});
    }
  }, [rowExtension, pageIndex, rowSelectionEnabled, setRowExpandedDelayed, setRowSelection]);
};
